import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { rupee, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loadRazorpay } from "@/lib/razorpay";
import { Spinner } from "@/components/States";
import { Milk, Leaf, Crown, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export function useMembership() {
  const { user } = useAuth();
  const cfg = useQuery({ queryKey: ["mem-config"], queryFn: () => api.get("/membership/config").then((r) => r.data) });
  const me = useQuery({ queryKey: ["mem-me"], queryFn: () => api.get("/membership/me").then((r) => r.data), enabled: !!user && user.role === "customer" });
  return { cfg: cfg.data, me: me.data, user };
}

export default function MembershipCard({ variant = "full" }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { cfg, me, user } = useMembership();
  const [busy, setBusy] = useState(false);
  if (!cfg || !cfg.enabled) return null;
  if (user && user.role !== "customer") return null;
  const active = me?.active;

  const buy = async () => {
    if (!user) return nav("/login");
    setBusy(true);
    try {
      const { data: init } = await api.post("/membership/purchase/create");
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setBusy(false); return; }
      const rzp = new window.Razorpay({
        key: init.key_id, amount: init.amount, currency: "INR", order_id: init.rzp_order_id,
        name: "Grofkart", description: `${cfg.name} membership · ${cfg.validity_days} days`,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone }, theme: { color: "#059669" },
        handler: async (resp) => {
          try { await api.post("/membership/purchase/verify", resp); toast.success(`${cfg.name} activated!`); qc.invalidateQueries({ queryKey: ["mem-me"] }); }
          catch (e) { toast.error(apiError(e)); }
          setBusy(false);
        },
        modal: { ondismiss: () => { setBusy(false); toast.error("Payment cancelled"); } },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const expiry = active ? me.membership.expires_at.slice(0, 10) : null;
  const compact = variant === "banner";
  return (
    <section data-testid="membership-card" className={`relative overflow-hidden rounded-2xl bg-slate-900 text-white ${compact ? "my-6 p-5" : "p-5"}`}>
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand/30 blur-3xl" />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="inline-flex items-center gap-1.5 rounded-full bg-amber-surge/20 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-300"><Crown className="h-3.5 w-3.5" />{cfg.name}</p>
          <h3 className="mt-2 font-head text-xl font-extrabold">{active ? "You're saving every day" : cfg.tagline}</h3>
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
            <span data-testid="membership-milk-discount" className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5"><Milk className="h-4 w-4 text-sky-300" />{cfg.milk_discount_percent}% off milk & dairy</span>
            <span data-testid="membership-veg-discount" className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5"><Leaf className="h-4 w-4 text-brand" />{cfg.veg_discount_percent}% off vegetables</span>
          </div>
          {active ? (
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-white/10 p-3"><p className="text-[11px] text-white/60">Total saved</p><p data-testid="membership-total-savings" className="font-mono text-lg font-bold text-brand">{rupee(me.total_savings)}</p></div>
              <div className="rounded-xl bg-white/10 p-3"><p className="text-[11px] text-white/60">Active till</p><p data-testid="membership-expiry" className="font-mono text-lg font-bold">{expiry}</p></div>
            </div>
          ) : !compact && (
            <ul className="mt-4 space-y-1 text-sm text-white/80">{(cfg.perks || []).map((p) => <li key={p} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand" />{p}</li>)}</ul>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <p className="font-mono text-2xl font-black">{rupee(cfg.price)}<span className="text-xs font-medium text-white/60"> / {cfg.validity_days} days</span></p>
          <button onClick={buy} disabled={busy} data-testid="membership-buy-btn" className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-transform hover:bg-brand-deep active:scale-95 disabled:opacity-60">
            {busy && <Spinner className="h-4 w-4 border-white" />}{active ? "Extend membership" : `Join ${cfg.name}`}
          </button>
          <p className="inline-flex items-center gap-1 text-[10px] text-white/50"><ShieldCheck className="h-3 w-3" /> Secure Razorpay payment</p>
        </div>
      </div>
    </section>
  );
}
